'use client';

import {
  createContext,
  useContext,
  useEffect,
  ReactNode,
  useState,
  useCallback,
  useMemo,
  Dispatch,
  SetStateAction,
  useRef,
} from 'react';
import { useFirebaseContext } from '@instamenu/context-firebase';
import posthog from 'posthog-js';
import { Address } from './address';
import { User } from './user';
import { apiClient } from '@instamenu/context-api';
import { pick } from 'lodash-es';
import {
  EmployerFragment,
  PaymentMethodsDocument,
  PaymentMethodsQuery,
} from './mutations.generated';

export type Employer = EmployerFragment;
export type UserContextProps =
  | {
      isAuthenticated: boolean;
      isAuthorized: boolean | null | undefined;
      ready: boolean;
      requestedPath: string;
      restaurants: EmployerFragment[];
      sortedRestaurants: EmployerFragment[];
      setRequestedPath: React.Dispatch<React.SetStateAction<string>>;
      signOut: () => void;
      user?: User;
      showModal: boolean;
      setShowModal: Dispatch<SetStateAction<boolean>>;
    }
  | undefined;

type UserProviderProps = {
  children: ReactNode;
  loadPaymentMethods?: boolean;
};

const identifyUser = (user?: User) => {
  if (!user) return posthog.reset();
  posthog.identify(user.id, pick(user, ['name', 'email', 'phone']));
};

export const UserContext = createContext<UserContextProps>(undefined);

export const UserProvider = ({ children, loadPaymentMethods = true }: UserProviderProps) => {
  const { firebaseUser, signOut: firebaseSignOut } = useFirebaseContext();
  const [user, setUser] = useState<User>();
  const [requestedPath, setRequestedPath] = useState<string>('/');
  const [showModal, setShowModal] = useState(false);
  const currentUpdateId = useRef(0);

  const isAuthenticated = !!firebaseUser;

  useEffect(() => {
    (async () => {
      if (!firebaseUser) {
        setUser(undefined);
      } else {
        // associate this user with the firebase id, move the local storage address to the user, and fetch the user data
        const address = Address.fromLocalStorage();
        const res = await User.init({
          phone: firebaseUser.phoneNumber ?? undefined,
          deliveryAddressesAttributes: address
            ? [address.asDeliveryAddressAttributes()]
            : undefined,
        });

        if (loadPaymentMethods) {
          const paymentMethodsResult = await apiClient()
            .query<PaymentMethodsQuery>(PaymentMethodsDocument, {})
            .toPromise();

          if (paymentMethodsResult.data?.paymentMethods) {
            res.setPaymentMethods(paymentMethodsResult.data.paymentMethods);
          }
        }

        setUser(res);
        Address.clearLocalStorage();
      }
    })();
  }, [firebaseUser, loadPaymentMethods]);

  useEffect(() => identifyUser(user), [user]);

  const authorizedRestaurants = useMemo(() => {
    if (!user) return [];
    const permissionsMap = user.permissions;
    return user.employers.filter((employer) => {
      const restaurantPermissions = permissionsMap[employer.id];
      return (
        restaurantPermissions &&
        (restaurantPermissions.includes('REPORTING') || restaurantPermissions.includes('SYSTEM'))
      );
    });
  }, [user]);

  const isAuthorized = useMemo(() => {
    if (!user) return false;
    const permissionsMap = user.permissions;
    return Object.keys(permissionsMap).length > 0;
  }, [user]);

  const sortedRestaurants = useMemo(
    () => [...authorizedRestaurants].sort((a, b) => a.name?.localeCompare(b.name ?? '') ?? 0),
    [authorizedRestaurants],
  );

  const ready = firebaseUser === null || !!user;

  const signOut = useCallback(() => {
    setUser(undefined);
    firebaseSignOut();
  }, [firebaseSignOut]);

  const value = {
    ready,
    isAuthenticated,
    isAuthorized,
    requestedPath,
    restaurants: authorizedRestaurants,
    sortedRestaurants,
    setRequestedPath,
    signOut,
    updating: currentUpdateId.current > 0,
    user,
    showModal,
    setShowModal,
  };
  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export function useUserContext() {
  const ctx = useContext(UserContext);
  if (!ctx) {
    throw new Error('useUserContext must be used within a UserProvider');
  }
  return ctx;
}
